/**
 * Shared "Export PDF / Export DOCX / Export As" control used by both the chronology header and
 * the cases-list row. Exports carry no options — the server applies the caller's own saved
 * preferences — so this control needs nothing but a case id and a format, and never fetches.
 * `variant` picks which presenter renders: the header keeps both format actions directly
 * clickable (`MedChronExportMenuInlineView`); the cases list uses one trigger with a
 * popover menu (`MedChronExportMenuDropdownView`). Both share this container's export
 * handling so the two entry points behave identically.
 */
import { ExportFormat, useExportCase } from "@/hooks/useExportCase";
import { isSessionExpiredError } from "@/services/apiService";
import { getErrorMessage } from "@/utils/errorUtils";
import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";

import { MedChronExportMenuDropdownView } from "./MedChronExportMenuDropdownView";
import { MedChronExportMenuInlineView } from "./MedChronExportMenuInlineView";
import type { MedChronExportMenuProps } from "./types";

export const MedChronExportMenu = ({
  caseId,
  caseName,
  variant,
  onCustomize,
  disabled = false,
  isCompact = false,
  onExportingChange,
}: MedChronExportMenuProps) => {
  const { exportCase, isExporting } = useExportCase();

  const isExportingPdf = isExporting(caseId, ExportFormat.PDF);
  const isExportingDocx = isExporting(caseId, ExportFormat.DOCX);

  useEffect(() => {
    onExportingChange?.(isExportingPdf || isExportingDocx);
  }, [isExportingPdf, isExportingDocx, onExportingChange]);

  const runExport = useCallback(
    (format: ExportFormat) => {
      exportCase(caseId, format, caseName)
        .then(() => toast.success("Export ready"))
        .catch((error) => {
          // The session flow re-authenticates on its own; a toast here would be noise.
          if (isSessionExpiredError(error)) return;
          toast.error(getErrorMessage(error));
        });
    },
    [exportCase, caseId, caseName]
  );

  const sharedViewProps = {
    isExportingPdf,
    isExportingDocx,
    disabled,
    onExportPdf: () => runExport(ExportFormat.PDF),
    onExportDocx: () => runExport(ExportFormat.DOCX),
    onCustomize,
  };

  if (variant === "list") {
    return <MedChronExportMenuDropdownView {...sharedViewProps} />;
  }

  return <MedChronExportMenuInlineView {...sharedViewProps} isCompact={isCompact} />;
};
